import React from "react";
import Skeleton from "@mui/material/Skeleton";

// Placeholders shown while the cart / delivery pages fetch their first
// data. Each one reuses the real component's class names, so spacing,
// borders and sizes come from the same CSS and nothing jumps on load.

const repeat = (count, render) =>
  Array.from({ length: count }, (_, i) => render(i));

const Pill = ({ width, sx }) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={42}
    sx={{ borderRadius: "24px", ...sx }}
  />
);

// Mirrors ProductCard
export const CartItemSkeleton = ({ count = 2 }) =>
  repeat(count, (i) => (
    <div className="ProductCard row no-gutters card" key={i}>
      <div className="image-container">
        <Skeleton
          variant="rectangular"
          width={157}
          height={190}
          sx={{ borderRadius: "12px 0 0 12px" }}
        />
      </div>
      <div className="col">
        <div className="product_name">
          <Skeleton variant="text" width="55%" />
        </div>
        <div className="price">
          <Skeleton variant="text" width={140} />
        </div>
        <div className="button-container">
          <Pill width={118} sx={{ mr: "10px" }} />
          <Pill width={142} />
          <Pill width={148} sx={{ ml: "15px" }} />
        </div>
      </div>
    </div>
  ));

const AddressBoxSkeleton = () => (
  <div className="checkout-address-card">
    <div className="checkout-address-card-header">
      <Skeleton
        variant="rounded"
        width={30}
        height={30}
        sx={{ borderRadius: "8px", flexShrink: 0 }}
      />
      <Skeleton variant="text" width={120} />
      <Skeleton
        variant="rounded"
        width={84}
        height={28}
        sx={{ ml: "auto", borderRadius: "8px" }}
      />
    </div>
    <div className="checkout-address-card-name">
      <Skeleton variant="text" width="50%" />
    </div>
    <div className="checkout-address-card-line">
      <Skeleton variant="circular" width={18} height={18} />
      <span style={{ flex: 1 }}>
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="45%" />
      </span>
    </div>
    <div className="checkout-address-card-line">
      <Skeleton variant="circular" width={18} height={18} />
      <Skeleton variant="text" width="40%" />
    </div>
  </div>
);

// Mirrors the selected-address panel in DeliveryCard
export const AddressSkeleton = () => (
  <div className="delivery-address-panel">
    <div className="delivery-address-panel-head">
      <div className="delivery-address-panel-who">
        <Skeleton variant="text" width={90} height={16} />
        <Skeleton variant="text" width={200} height={30} />
      </div>
      <div className="delivery-address-panel-actions">
        <Skeleton
          variant="rounded"
          width={78}
          height={34}
          sx={{ borderRadius: "10px" }}
        />
        <Skeleton
          variant="rounded"
          width={92}
          height={34}
          sx={{ borderRadius: "10px" }}
        />
      </div>
    </div>
    <div className="checkout-address-grid is-horizontal">
      <AddressBoxSkeleton />
      <AddressBoxSkeleton />
    </div>
  </div>
);

// Mirrors a delivery-speed option row (rendered inside .delivery-speed-list)
export const DeliveryTypeSkeleton = ({ count = 2 }) =>
  repeat(count, (i) => (
    <div className="delivery-type-container-row delivery-speed-option" key={i}>
      <div className="delivery-type-radio">
        <Skeleton variant="circular" width={18} height={18} />
      </div>
      <div className="delivery-type-des">
        <Skeleton variant="text" width="30%" height={26} />
        <Skeleton variant="text" width="45%" />
        <Skeleton variant="text" width="85%" />
      </div>
    </div>
  ));

const SummaryRow = ({ className = "order-subtitle-row" }) => (
  <div className={className}>
    <Skeleton variant="text" width="45%" />
    <Skeleton variant="text" width="22%" />
  </div>
);

// Mirrors BillingAddress (cart page order summary)
export const CartSummarySkeleton = ({ items = 2 }) => (
  <div className="BillingAddress">
    <div className="order-title">Order Summary</div>
    <div className="contents">
      {repeat(items, (i) => (
        <SummaryRow className="order-subtitle-row custom" key={i} />
      ))}
      <SummaryRow />
      <SummaryRow />
      <Skeleton
        variant="rounded"
        height={96}
        sx={{ mb: "20px", borderRadius: "6px" }}
      />
      <Skeleton
        variant="rounded"
        height={44}
        sx={{ mb: "20px", borderRadius: "24px" }}
      />
    </div>
  </div>
);

// Mirrors OrderDetails (delivery / payment page order summary)
export const OrderSummarySkeleton = ({ items = 2 }) => (
  <div className="BillingAddress">
    <div className="order-title">Order Summary</div>
    {repeat(items, (i) => (
      <div className="bookdetails-row" key={i}>
        <div className="image-container">
          <Skeleton variant="rectangular" width={90} height={109} />
        </div>
        <div
          className="col"
          style={{ display: "flex", flexDirection: "column", gap: "10px" }}
        >
          <Skeleton variant="text" width="70%" height={26} />
          <Skeleton variant="text" width="45%" />
        </div>
      </div>
    ))}
    <div className="contents">
      <SummaryRow className="order-subtitle-row-border" />
      <SummaryRow />
    </div>
  </div>
);
